import { Spacing } from '@persona-id/design-tokens';
import { PersonaIcon } from '@persona-id/design-tokens/icons';
import { startCase } from 'lodash';
import { useCallback, VFC } from 'react';
import styled from 'styled-components';

import ReactSelect from 'dashboard/components/ReactSelect';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import StepConfigTypeCard from 'dashboard/components/WorkflowControlPanel/StepConfigTypeCard';
import TriggerAdvancedConfigurationPanel from 'dashboard/components/WorkflowControlPanel/TriggerAdvancedConfigurationPanel/TriggerAdvancedConfigurationPanel';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import { TriggerObjectType, TriggerObjectTypes, TriggerSchedule } from 'dashboard/models/trigger';
import { WorkflowError } from 'dashboard/models/workflow';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { headingMixins } from 'design-system/mixins/text';

const SubTitle = styled.div`
  ${headingMixins.sm}
  padding-bottom: ${Spacing[2]};
`;

const triggerObjectTypeOptions = TriggerObjectTypes.map((one) => {
  return {
    value: one,
    label: startCase(one),
  };
});

interface TriggerConfigScheduleProps {
  trigger: TriggerSchedule;
  editable: boolean;
  errors: WorkflowError[];
  handleClose: () => void;
  updateTrigger: (newTrigger: TriggerSchedule) => void;
  workflowVersionExpiration: number;
  updateWorkflowVersionExpiration: (expiration: number) => void;
}

const TriggerConfigSchedule: VFC<TriggerConfigScheduleProps> = ({
  trigger,
  editable,
  errors,
  handleClose,
  updateTrigger,
  workflowVersionExpiration,
  updateWorkflowVersionExpiration,
}) => {
  const updateTriggerScheduleObjectType = useCallback(
    (newType: TriggerObjectType) => {
      updateTrigger({
        ...trigger,
        attributes: {
          ...trigger.attributes,
          'trigger-object-type': newType,
        },
      });
    },
    [trigger, updateTrigger],
  );
  const { hasErrorFor, renderErrorMessageFor } =
    useWorkflowError<keyof TriggerSchedule['attributes']>(errors);

  return (
    <>
      <Spacer height={2} />
      <StepConfigTypeCard
        icon={<PersonaIcon size={24} />}
        title='Scheduled Trigger'
        description='Trigger on schedule'
      />
      <Spacer height={2} />
      <SubTitle>Configuration</SubTitle>
      <ParamLabel
        labelText='Trigger Object Type'
        htmlFor='trigger-config-schedule__object-type-select-input'
      />
      <ReactSelect
        id='trigger-config-schedule__object-type-select'
        value={trigger.attributes['trigger-object-type']}
        onChange={updateTriggerScheduleObjectType}
        options={triggerObjectTypeOptions}
        isSearchable={true}
        portal={true}
        disabled={!editable}
        hasError={hasErrorFor('trigger-object-type')}
      />
      {renderErrorMessageFor('trigger-object-type')}
      <Spacer height={2} />
      <TriggerAdvancedConfigurationPanel
        editable={editable}
        workflowVersionExpiration={workflowVersionExpiration}
        updateWorkflowVersionExpiration={updateWorkflowVersionExpiration}
      />
      <Spacer height={3} />
      <HStack equal={true}>
        <Button
          id='trigger-config-schedule__close-button'
          aria-label='Close panel'
          onClick={handleClose}
        >
          Close
        </Button>
      </HStack>
    </>
  );
};

export default TriggerConfigSchedule;
